import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://hse.erdenetmc.mn/api",
});

api.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `SSO ${token}`;
  }
  return config;
});

/**
 * Хяналтын арга хэмжээний БИЕЛЭЛТ.
 *
 * Хариуцагч «биелүүлсэн» гэж бүртгээд, ХАБЭА баталгаажуулсны дараа л
 * хэрэгжсэнд тооцогдоно (өөрийгөө батлах боломжгүй).
 */
export const xabRiskMeasureApi = {
  progress: (params) =>
    api.get("/xab/risk-measure-progress", { params }).then((r) => r.data),
  complete: (id, data) =>
    api.post(`/xab/risk-measures/${id}/complete`, data).then((r) => r.data),
  verify: (id, data) =>
    api.post(`/xab/risk-measures/${id}/verify`, data).then((r) => r.data),
};

/** БН/хэсгийн эрсдэлийн үнэлгээний ХЭВЛЭХ тайлан (тайлангууд/эрсдэл БН).
 *  ⚠ `/xab/risk-report/summary` (удирдлагын нэгтгэл)-ЭЭС ӨӨР endpoint. */
/** ХЭМАБМТ-02 4.3.7 — нэгжийн үнэлгээтэй АЖИЛ, ҮЙЛЧИЛГЭЭ + эрсдэлийн эрэмбэ.
 *  Наряд-даалгавар дээр ажлаа эндээс сонгоно; 25 оноотой нь `blocked`. */
export const xabRiskWorksApi = {
  list: (params) => api.get("/xab/risk-works", { params }).then((r) => r.data),
  /** Ээлжит бус үнэлгээний 9 үндэслэл (4.1.8) */
  triggers: () => api.get("/xab/risk-triggers").then((r) => r.data),
};

export const xabRiskUnitReportApi = {
  get: (params) =>
    api.get("/xab/risk-unit-report", { params }).then((r) => r.data),
  /** Бүх нэгжийн нэгдсэн үнэлгээ + эрсдэл бууруулах арга хэмжээний төлөвлөгөө */
  plan: (params) =>
    api.get("/xab/risk-consolidated-plan", { params }).then((r) => r.data),
};

/**
 * ЭРСДЭЛИЙН ТАЙЛАНГИЙН ХӨЛ ТОЛГОЙ — `/risk/reports`-ийн таб тус бүрд.
 *
 * `report_key`: "unit" | "plan" | "hazards"
 * Хамрах хүрээ = (report_key, tseh_code, bn_code, year) — он/нэгж тутамд
 * тусдаа хадгалагдана (батлагч солигдоход өмнөх онуудын баримт
 * хойшлон өөрчлөгдөхгүй).
 *
 * ⚠ Хүснэгт (`xab.risk_report_head`) байхгүй байж болно — migration
 *   гараар ажилладаг. Тийм үед `head_ready: false` ирж, GET нь АНХДАГЧ
 *   утгуудыг буцаана; PUT нь 409 буцаана.
 * ⚠ Засах нь зөвхөн бүрэн эрхтэйд (`can_edit`) — сервер 403 буцаана.
 */
export const xabRiskReportHeadApi = {
  get: (params) =>
    api.get("/xab/risk-report-head", { params }).then((r) => r.data),
  save: (scope, head) =>
    api.put("/xab/risk-report-head", { ...scope, head }).then((r) => r.data),
};

export const xabRiskApi = {
  list: (params) =>
    api.get("/xab/risk-assessments", { params }).then((r) => r.data),
  get: (id) => api.get(`/xab/risk-assessments/${id}`).then((r) => r.data),
  create: (data) => api.post("/xab/risk-assessments", data).then((r) => r.data),
  update: (id, d) =>
    api.put(`/xab/risk-assessments/${id}`, d).then((r) => r.data),
  delete: (id) => api.delete(`/xab/risk-assessments/${id}`).then((r) => r.data),
  submit: (id) =>
    api.post(`/xab/risk-assessments/${id}/submit`).then((r) => r.data),
  approve: (id) =>
    api.post(`/xab/risk-assessments/${id}/approve`).then((r) => r.data),
  reject: (id) =>
    api.post(`/xab/risk-assessments/${id}/reject`).then((r) => r.data),
  // Group — нэг ажил үйлчилгээ = олон аюулын мөр
  getGroup: (code) =>
    api.get(`/xab/risk-assessments/group/${code}`).then((r) => r.data),
  createGroup: (data) =>
    api.post("/xab/risk-assessments/group", data).then((r) => r.data),
  updateGroup: (code, data) =>
    api.put(`/xab/risk-assessments/group/${code}`, data).then((r) => r.data),
  submitGroup: (code) =>
    api.post(`/xab/risk-assessments/group/${code}/submit`).then((r) => r.data),
  approveGroup: (code) =>
    api.post(`/xab/risk-assessments/group/${code}/approve`).then((r) => r.data),
  rejectGroup: (code) =>
    api.post(`/xab/risk-assessments/group/${code}/reject`).then((r) => r.data),
  // 2-р шатны хяналт — бүртгэл (ажил үйлчилгээ) түвшинд 2-оос-3 дүрийн батлалт.
  // payload: { decision: "APPROVE"|"RETURN"|"REJECT", comment }
  groupReview: (code, payload) =>
    api
      .post(`/xab/risk-assessments/group/${code}/review`, payload)
      .then((r) => r.data),
  // 2-р шатны санал болгосон оноо — аюул (мөр) тус бүрд, төлөв өөрчлөхгүй.
  // payload: { proposed_probability, proposed_consequence }
  saveProposedScore: (id, payload) =>
    api
      .post(`/xab/risk-assessments/${id}/proposed-score`, payload)
      .then((r) => r.data),
  getMeasures: (id) =>
    api.get(`/xab/risk-assessments/${id}/measures`).then((r) => r.data),
  addMeasure: (id, d) =>
    api.post(`/xab/risk-assessments/${id}/measures`, d).then((r) => r.data),
  updateMeasure: (mid, d) =>
    api.put(`/xab/risk-measures/${mid}`, d).then((r) => r.data),
  deleteMeasure: (mid) =>
    api.delete(`/xab/risk-measures/${mid}`).then((r) => r.data),
  /* ── АЖИЛ ҮЙЛЧИЛГЭЭНИЙ БУУРУУЛАХ ТӨЛӨВЛӨГӨӨ (ХЭМАБМТ-02 5.7) ────────
     Хариуцагч · хугацаа · бууруулах түвшин · үндэслэл · зардал нь АЖЛЫН
     БҮРТГЭЛ дээр НЭГ утгаар хадгалагдана (арга хэмжээний эзнээс тусдаа).
     ⚠ Жагсаалт татахдаа тусад дуудах шаардлагагүй — мөр бүр дээр
       `work_plan` гэж ирнэ. Энэ нь ЗӨВХӨН хадгалах зам. */
  updateWorkPlan: (code, d) =>
    api.put(`/xab/risk-work-plan/${code}`, d).then((r) => r.data),
  /** Ажил үйлчилгээний нэгдсэн дүгнэлт (/risk/works) — items + summary */
  workReview: (params) =>
    api.get("/xab/risk-work-review", { params }).then((r) => r.data),
  level2Review: (id, payload) =>
    api
      .post(`/xab/risk-assessments/${id}/level2-review`, payload)
      .then((r) => r.data),
  bnApprove: (id, payload) =>
    api
      .post(`/xab/risk-assessments/${id}/bn-approve`, payload)
      .then((r) => r.data),
  /* Батлалтыг буцаах (approved → reviewing). `note` ЗААВАЛ — сервер
     шалтгаангүй бол 400 буцаана, аудитад үлдээх зорилготой. */
  bnUnapprove: (id, note) =>
    api
      .post(`/xab/risk-assessments/${id}/bn-unapprove`, { note })
      .then((r) => r.data),
  sendToHse: (payload) =>
    api
      .post("/xab/risk-submissions/hse-department", payload)
      .then((r) => r.data),
  sendToCommittee: (payload) =>
    api.post("/xab/risk-submissions/committee", payload).then((r) => r.data),
  sendToManagement: (payload) =>
    api.post("/xab/risk-submissions/management", payload).then((r) => r.data),
  mgmtApprove: (id, payload) =>
    api
      .post(`/xab/risk-assessments/${id}/mgmt-approve`, payload || {})
      .then((r) => r.data),
  mgmtReject: (id, payload) =>
    api
      .post(`/xab/risk-assessments/${id}/mgmt-reject`, payload || {})
      .then((r) => r.data),
  listSubmissions: (params) =>
    api.get("/xab/risk-submissions", { params }).then((r) => r.data),
  getSubmission: (sid) =>
    api.get(`/xab/risk-submissions/${sid}`).then((r) => r.data),
  receiveSubmission: (sid) =>
    api.post(`/xab/risk-submissions/${sid}/receive`).then((r) => r.data),
  listCapitalRequests: (params) =>
    api.get("/xab/risk-capital-requests", { params }).then((r) => r.data),
  decideCapital: (crid, payload) =>
    api
      .post(`/xab/risk-capital-requests/${crid}/decide`, payload)
      .then((r) => r.data),
  listCapitalRequestVotes: (crid) =>
    api
      .get(`/xab/risk-capital-requests/${crid}/votes`)
      .then((r) => r.data),
};

// ── ЖИЛИЙН АЮУЛЫН ЖАГСААЛТ ────────────────────────────────────────
// Батлагдсан ЭТ-ээс эрсдэлийн оноогоор эрэмбэлж сонгоод ЭҮХ баталгаажуулна.
// Backend: Resources/XAB/annual_hazard.py
export const xabAnnualHazardApi = {
  /** Тухайн жилийн жагсаалт + мөрүүд + саналын хураангуй.
   *  Жагсаалт байхгүй бол backend ноорог АВТОМАТААР үүсгэнэ. */
  get: (year) =>
    api
      .get("/xab/annual-hazard-lists", { params: year ? { year } : {} })
      .then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/annual-hazard-lists/${id}`, payload).then((r) => r.data),
  /** ТУХАЙН ЖИЛД МӨРДӨГДӨХ (батлагдсан) жагсаалт — лавлахын хуудас уншина.
   *  `approved:false` ирвэл тухайн жилд батлагдсан жагсаалт байхгүй гэсэн
   *  үг: дуудагч нь лавлахаа хэвээр харуулна. */
  effective: (year) =>
    api
      .get("/xab/annual-hazard-lists/effective", {
        params: year ? { year } : {},
      })
      .then((r) => r.data),
  /** ХӨРВҮҮЛЭХ нэр дэвшигчид — өмнөх жилийн БАТЛАГДСАН жагсаалт,
   *  эсвэл эхний жилд АЮУЛЫН НЭГДСЭН ЛАВЛАХ (/risk/reference/factor).
   *  `source.kind` нь "list" эсвэл "reference" гэж алийг нь буцаасныг заана. */
  carryCandidates: (id, params) =>
    api
      .get(`/xab/annual-hazard-lists/${id}/carry-candidates`, { params })
      .then((r) => r.data),
  /** Сонгосныг дараа жилийн жагсаалтад хөрвүүлнэ.
   *  ⚠ ХОЁР ӨӨР ТҮЛХҮҮР — андуурвал юу ч нэмэгдэхгүй:
   *     item_ids   — ӨМНӨХ ЖАГСААЛТЫН мөрийн id (source.kind === "list")
   *     hazard_ids — АЮУЛЫН ЛАВЛАХЫН id        (source.kind === "reference") */
  carry: (id, ids, kind) =>
    api
      .post(
        `/xab/annual-hazard-lists/${id}/carry`,
        kind === "reference" ? { hazard_ids: ids } : { item_ids: ids },
      )
      .then((r) => r.data),
  /** Лавлахад байгаа, жагсаалтад хараахан ороогүй аюулууд + ангиллын жагсаалт. */
  addableHazards: (id) =>
    api.get(`/xab/annual-hazard-lists/${id}/items`).then((r) => r.data),
  /** Шинэ аюул нэмэх. Хоёр хэлбэр:
   *    addItems(id, [12, 13])                  — лавлахаас сонгосон
   *    addItems(id, null, { name, category_id }) — лавлахад ч байхгүй шинэ */
  addItems: (id, hazardIds, newHazard) =>
    api
      .post(`/xab/annual-hazard-lists/${id}/items`, {
        hazard_ids: hazardIds || [],
        ...(newHazard ? { new: newHazard } : {}),
      })
      .then((r) => r.data),
  removeItem: (itemId) =>
    api.delete(`/xab/annual-hazard-list-items/${itemId}`).then((r) => r.data),
  submit: (id) =>
    api.post(`/xab/annual-hazard-lists/${id}/submit`).then((r) => r.data),
  /** ЭҮХ гишүүний санал: { decision: "APPROVE"|"REJECT", comment } */
  vote: (id, payload) =>
    api.post(`/xab/annual-hazard-lists/${id}/vote`, payload).then((r) => r.data),
  approve: (id) =>
    api.post(`/xab/annual-hazard-lists/${id}/approve`, {}).then((r) => r.data),
  reject: (id, comment) =>
    api
      .post(`/xab/annual-hazard-lists/${id}/reject`, { comment })
      .then((r) => r.data),
};

// ── ЭҮХ-ны гишүүний санал ────────────────────────────────────────
export const xabCommitteeVoteApi = {
  list: (params) =>
    api.get("/xab/committee-votes", { params }).then((r) => r.data),
  get: (vid) => api.get(`/xab/committee-votes/${vid}`).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/committee-votes", payload).then((r) => r.data),
  update: (vid, payload) =>
    api.put(`/xab/committee-votes/${vid}`, payload).then((r) => r.data),
  delete: (vid) =>
    api.delete(`/xab/committee-votes/${vid}`).then((r) => r.data),
};

// ── ЭҮХ-ны гишүүний лавлах ────────────────────────────────────────
export const xabCommitteeMemberApi = {
  list: (params) =>
    api.get("/xab/committee-members", { params }).then((r) => r.data),
  get: (mid) => api.get(`/xab/committee-members/${mid}`).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/committee-members", payload).then((r) => r.data),
  update: (mid, payload) =>
    api.put(`/xab/committee-members/${mid}`, payload).then((r) => r.data),
  delete: (mid) =>
    api.delete(`/xab/committee-members/${mid}`).then((r) => r.data),
};

// ── МХ-ны гишүүдийн санал ──────────────────────────────────────────
export const xabMgmtCommitteeVoteApi = {
  list: (params) =>
    api.get("/xab/mgmt-committee-votes", { params }).then((r) => r.data),
  get: (vid) =>
    api.get(`/xab/mgmt-committee-votes/${vid}`).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/mgmt-committee-votes", payload).then((r) => r.data),
  update: (vid, payload) =>
    api.put(`/xab/mgmt-committee-votes/${vid}`, payload).then((r) => r.data),
  delete: (vid) =>
    api.delete(`/xab/mgmt-committee-votes/${vid}`).then((r) => r.data),
};

// ── МХ-ны гишүүний лавлах ──────────────────────────────────────────
export const xabMgmtCommitteeMemberApi = {
  list: (params) =>
    api.get("/xab/mgmt-committee-members", { params }).then((r) => r.data),
  get: (mid) =>
    api.get(`/xab/mgmt-committee-members/${mid}`).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/mgmt-committee-members", payload).then((r) => r.data),
  update: (mid, payload) =>
    api.put(`/xab/mgmt-committee-members/${mid}`, payload).then((r) => r.data),
  delete: (mid) =>
    api.delete(`/xab/mgmt-committee-members/${mid}`).then((r) => r.data),
};

// ── ЭҮХ / МХ бүрэлдэхүүнийг баталсан ТУШААЛ (тайлант жилээр) ─────────
export const xabCommitteeOrderApi = {
  list: (params) => api.get("/xab/committee-orders", { params }).then((r) => r.data),
  create: (payload) => api.post("/xab/committee-orders", payload).then((r) => r.data),
  update: (id, payload) => api.put(`/xab/committee-orders/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/xab/committee-orders/${id}`).then((r) => r.data),
  /** Тушаалын эх хувь (PDF/скан) — MinIO risk/committee-orders/ */
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/xab/committee-orders/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
  },
};

// ── ХЭМАБМТ-06 дотоод хяналт шалгалт (I/II/III шат) ────────────────
// Хяналт шалгалтын dashboard — шатаар / зөрчлийн төрлөөр биелэлт
export const xabInspectionDashboardApi = {
  get: (params = {}) =>
    api.get("/xab/inspection-dashboard", { params }).then((r) => r.data),
};

// ── Нэг зөрчил → олон мастерт хувиарлах, тус бүрийн гүйцэтгэл ────────
export const xabItemAssignmentApi = {
  list: (itemId) =>
    api.get(`/xab/inspection-items/${itemId}/assignments`).then((r) => r.data),
  /** Хувиарлалтыг БҮХЭЛД нь орлуулна: { items: [...] } */
  save: (itemId, payload) =>
    api.post(`/xab/inspection-items/${itemId}/assignments`, payload).then((r) => r.data),
  /** Мастер өөрийн ажлын гүйцэтгэлийг оруулна: { percent, note, photo_url } */
  progress: (assignmentId, payload) =>
    api
      .post(`/xab/inspection-assignments/${assignmentId}/progress`, payload)
      .then((r) => r.data),
  /** Шалгагч/ХАБЭА: { action: "accept"|"reject", note } */
  verify: (assignmentId, payload) =>
    api
      .post(`/xab/inspection-assignments/${assignmentId}/verify`, payload)
      .then((r) => r.data),
};

// ── Зөрчлийн бүртгэлийн төрлийн хяналт (/incident/type) ──────────────
// ⚠ Дээрх `inspection-findings`-ЭЭС ӨӨР эх сурвалж: энэ нь ZR- зөрчлийн
//   бүртгэл (hazard_reports), тэр нь шалгах хуудасны мөр.
export const xabViolationMonitorApi = {
  list: (params = {}) =>
    api.get("/xab/violation-monitor", { params }).then((r) => r.data),
};

// ── Бүх шалгалтын зөрчлийн нэгдсэн хяналт (/inspection/types) ────────
export const xabInspectionFindingsApi = {
  list: (params = {}) =>
    api.get("/xab/inspection-findings", { params }).then((r) => r.data),
  /** Нэг зөрчилд төрөл / хариуцагч / хугацаа оноох */
  assign: (itemId, payload) =>
    api.post(`/xab/inspection-items/${itemId}/assign`, payload).then((r) => r.data),
};

export const xabInspectionApi = {
  // Лавлах
  getStages: () => api.get("/xab/inspection-stages").then((r) => r.data),
  /* Админын харагдац — {items, can_manage}. ⚠ `getStages` нь массив
     хэвээр (stageRef, товчнууд массив хүлээдэг). */
  getStagesAdmin: () =>
    api
      .get("/xab/inspection-stages", { params: { all: 1 } })
      .then((r) => r.data),
  /* Шатны ХАРАГДАХ мэдээллийг засна (нэмэх/устгах байхгүй) */
  saveStage: (body) =>
    api.put("/xab/inspection-stages", body).then((r) => r.data),
  /* Шат идэвхгүй болгох — ЗӨВХӨН хөгжүүлэгч (физикээр устгахгүй) */
  deleteStage: (id) =>
    api.delete("/xab/inspection-stages", { params: { id } }).then((r) => r.data),
  getTemplate: (params) =>
    api.get("/xab/checklist-templates", { params }).then((r) => r.data),
  /** ХООСОН нэмэлт зөрчлийн мөрийг устгах (2026-11-02).
   *  «Өөр зөрчил» дараад бөглөөгүй орхисон мөрийг арилгана.
   *  ⚠ Сервер нь ЗӨВХӨН хоосон БА илүүдэл мөрийг зөвшөөрнө —
   *    үзүүлэлтийн цорын ганц мөр, бичсэн зөрчил устахгүй. */
  deleteItemRow: (rowId) =>
    api.delete(`/xab/inspection-item-rows/${rowId}`).then((r) => r.data),
  /** ── БН-ИЙН ӨӨРИЙН ШАЛГАХ ХУУДАС (2026-10-29) ──────────────────
   *  Урсгал: draft → pending (илгээх) → approved | rejected.
   *  ⚠ POST хэрэглэнэ, PATCH БИШ — CORS-ийн зөвшөөрөгдсөн методын
   *    жагсаалтад (`app.py`) PATCH байхгүй. */
  bnTemplates: (params) =>
    api.get("/xab/bn-checklist-templates", { params }).then((r) => r.data),
  bnTemplateCreate: (payload) =>
    api.post("/xab/bn-checklist-templates", payload).then((r) => r.data),
  bnTemplateUpdate: (id, payload) =>
    api.put(`/xab/bn-checklist-templates/${id}`, payload).then((r) => r.data),
  bnTemplateRemove: (id) =>
    api.delete(`/xab/bn-checklist-templates/${id}`).then((r) => r.data),
  bnTemplateSubmit: (id) =>
    api
      .post(`/xab/bn-checklist-templates/${id}/submit`, {})
      .then((r) => r.data),
  bnTemplateApprove: (id, payload) =>
    api
      .post(`/xab/bn-checklist-templates/${id}/approve`, payload || {})
      .then((r) => r.data),
  // Шалгах хуудасны толгойг удирдах — IV шатанд мэргэжилтэн тус бүр өөрийн
  // хуудастай тул нэмэх/засах шаардлагатай (зөвхөн ХАБЭА хэлтэс)
  createTemplate: (payload) =>
    api.post("/xab/checklist-templates", payload).then((r) => r.data),
  updateTemplate: (id, payload) =>
    api.put(`/xab/checklist-templates/${id}`, payload).then((r) => r.data),
  removeTemplate: (id) =>
    api.delete(`/xab/checklist-templates/${id}`).then((r) => r.data),
  // Ноцтой зөрчил (ХЭМАБМТ-06 Хавс.№1) — массив (шалгах маягтад)
  getSeriousViolations: () =>
    api
      .get("/xab/serious-violations")
      .then((r) => (Array.isArray(r.data) ? r.data : r.data?.items || [])),
  // Лавлах удирдах — { items, can_manage, source }
  seriousViolations: {
    list: () => api.get("/xab/serious-violations").then((r) => r.data),
    create: (d) => api.post("/xab/serious-violations", d).then((r) => r.data),
    update: (id, d) => api.put(`/xab/serious-violations/${id}`, d).then((r) => r.data),
    remove: (id) => api.delete(`/xab/serious-violations/${id}`).then((r) => r.data),
  },
  /** Зөрчлийн хариуцагчийг БН-ийн бүтцээс хайх (нэр / ТН / регистр) */
  searchWorkers: (params) =>
    api.get("/xab/workers", { params }).then((r) => r.data),
  // Шалгалт
  list: (params) => api.get("/xab/inspections", { params }).then((r) => r.data),
  get: (id) => api.get(`/xab/inspections/${id}`).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/inspections", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/inspections/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/xab/inspections/${id}`).then((r) => r.data),
  /** Устгахаас ӨМНӨ дэд бүртгэлийн тоог асууна — ЮУ Ч устгахгүй.
   *  Хэрэглэгч: «устгах үед хэрвээ саб бүртгэлтэй бол анхааруулга өгч
   *  давхар устгах хэрэгтэй». */
  removePreview: (id) =>
    api
      .delete(`/xab/inspections/${id}`, { params: { preview: 1 } })
      .then((r) => r.data),
  // payload: { force, proposal_discipline, proposal_reward } — force нь
  // оноогдсон бүлэг бөглөгдөөгүй сануулгыг (409) давна
  close: (id, payload) =>
    api.post(`/xab/inspections/${id}/close`, payload || {}).then((r) => r.data),
  /* Хаагдсан шалгалтыг БУЦААХ — зөвхөн ХАБЭА хэлтэс, шалтгаан заавал.
     Сервер нь шалтгаангүй бол 400, эрхгүй бол 403 буцаана. */
  reopen: (id, note) =>
    api
      .post(`/xab/inspections/${id}/reopen`, { note })
      .then((r) => r.data),
  // ── ДҮНГИЙН ХУРЛЫН ХОЁР ШАТ (ХЭМАБМТ-06 4.1.4.3) ──
  //   III/IV шатны үнэлгээг шалгагч дангаараа тогтоохгүй — хурал тогтооно.
  //   submit: үр дүнг хуралд илгээнэ (`status` нь `open` ХЭВЭЭР)
  //   approve: КОМИССЫН ДАРГА үнэлгээг батлаад шалгалтыг хаана
  // payload(submit):  { closing_meeting_date, meeting_no, secretary_tn }
  // payload(approve): { meeting_no, closing_meeting_date,
  //                     eval_override, eval_override_reason }
  submitToMeeting: (id, payload) =>
    api.post(`/xab/inspections/${id}/submit`, payload || {}).then((r) => r.data),
  // Илгээснээ БУЦААХ — санамсаргүй дарсан шалгагч гацахгүйн тулд.
  // Батлагдсаныг буцаахгүй (тэр нь хурлын шийдвэр).
  withdrawFromMeeting: (id) =>
    api
      .post(`/xab/inspections/${id}/submit`, { withdraw: true })
      .then((r) => r.data),
  approveMeeting: (id, payload) =>
    api.post(`/xab/inspections/${id}/approve`, payload || {}).then((r) => r.data),
  // III шатны комисс — бүлэг бүрд хариуцагч гишүүн (ХЭМАБМТ-06 2.5.1-2.5.3)
  getCommission: (id) =>
    api.get(`/xab/inspections/${id}/commission`).then((r) => r.data),
  setCommission: (id, payload) =>
    api.put(`/xab/inspections/${id}/commission`, payload).then((r) => r.data),
  /** Лавлахаас БҮЛГИЙГ шинэчлэх — бүрэлдэхүүнийг УСТГАХГҮЙ (PATCH).
   *  ⚠ Шалгалтын комисс нь ҮҮСГЭХ ҮЕИЙН хуулбар тул лавлахын дараачийн
   *    засвар түүнд ХҮРДЭГГҮЙ. Үүнгүйгээр хэрэглэгч шалгалтыг бүхэлд нь
   *    устгаж дахин үүсгэх л гарц байв (2026-10-29-нд 2 шалгалт тийнхүү
   *    устав). */
  /** ⚠ POST, PATCH БИШ: CORS-ийн зөвшөөрөгдсөн методын жагсаалтад
   *    (`app.py`) PATCH байхгүй тул preflight дээр блоклогддог. */
  syncCommission: (id) =>
    api.post(`/xab/inspections/${id}/commission`, {}).then((r) => r.data),
  suggestCommission: (params) =>
    api.get("/xab/inspection-commission/suggest", { params }).then((r) => r.data),
  addFile: (payload) =>
    api.post("/xab/inspections/file", payload).then((r) => r.data),
  deleteFile: (id) =>
    api.delete("/xab/inspections/file", { data: { id } }).then((r) => r.data),
};

// ── БН-ийн III ШАТНЫ БАЙНГЫН КОМИСС — лавлах (ХЭМАБМТ-06 2.5.1-2.5.3) ────
// БН тус бүр нэг удаа бүрдүүлнэ; III шатны шалгалт үүсэхэд сервер эндээс
// inspection_commission-д хуулна. tseh_code = ЦЕХ/ХЭЛТЭС (А-конвенц).
export const xabBnCommissionApi = {
  list: (params) =>
    api.get("/xab/bn-commissions", { params }).then((r) => r.data),
  // {tseh_code, tseh_name, commission_no, members:[{tn, member_role, group_ids}]}
  save: (payload) =>
    api.put("/xab/bn-commissions", payload).then((r) => r.data),
  remove: (tseh_code, commission_no) =>
    api
      .delete("/xab/bn-commissions", { params: { tseh_code, commission_no } })
      .then((r) => r.data),
  mine: () => api.get("/xab/bn-commissions/mine").then((r) => r.data),
  /** ХЭН ХЭЗЭЭ ЮУ хийсэн — бүрэлдэхүүний хувилбарууд + зөрүү.
      ⚠ Тусдаа аудитын хүснэгт БИШ: хадгалалт нь мөрүүдийг soft-delete
        хийгээд дахин үүсгэдэг тул түүх баазад аль хэдийн бий. */
  history: (params) =>
    api.get("/xab/bn-commissions/history", { params }).then((r) => r.data),
};

// ── ЧУХАЛ ХЯНАЛТЫН ШАЛГАХ ХУУДАС (15 критик хяналт) ────────────────────
// Бүртгэл нь `hazard_reports`-д (page_kind='inspection', төрөл
// critical_control) ирээд `xab.critical_control_sheet`-д задардаг. Энэ
// endpoint нь хураангуйг НЭГ query-гээр өгнө — вэб ба гар утаснаас
// бүртгэсэн хуудас ялгаагүй нэг жагсаалтад.
export const xabCriticalControlApi = {
  list: (params) =>
    api.get("/xab/critical-control-sheets", { params }).then((r) => r.data),
};

// ── УДИРДЛАГЫН МАНЛАЙЛАЛ — анхан шатны удирдах ажилтны автомат үнэлгээ ──
// ХЭМАБМТ-10 хавсралт №5-ын шалгуурыг хэсэг тус бүрд системийн бүртгэлээс
// тооцно (Resources/XAB/leadership_score.py). Дээд оноо нь /reward/reference
// лавлахын MGR_FL чадамжаас ирнэ.
export const xabLeadershipApi = {
  score: (params) =>
    api.get("/xab/leadership-score", { params }).then((r) => r.data),
};

// ── Хяналт шалгалтын ЛАВЛАХ — шалгах хуудасны үзүүлэлт ─────────────────
// Журмын стандарт мөр (bn_code=null) + бүтцийн нэгжийн нэмэлт. Нэмэлтийг
// цехийн ХАБ инженер оруулж, цехийн дарга батална.
export const xabChecklistRefApi = {
  // { template, items[], summary{total,standard,custom,pending,draft,rejected} }
  list: (params) =>
    api.get("/xab/checklist-items", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/checklist-items", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/checklist-items/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/checklist-items/${id}`).then((r) => r.data),
};

// ── ШАЛГАХ ХУУДСЫГ ЗААЛТААР БӨГЛӨХ ─────────────────────────────────────
// Үзүүлэлтийн ЧӨЛӨӨТ эшлэл («ЦХАБДД-ийн 14.3») нь журмын лавлахын
// заалттай холбогдоогүй байдаг. `suggest` нь угтвар (журмын нэрний
// үсгийн тэмдэглэгээ) + дугаараар тулгаж САНАЛ буцаана; `apply` нь
// хэрэглэгч баталсныг Л бичнэ.
// ⚠ Сервер нь `rule_ref`-д гар хүрэхгүй — зөвхөн `clause_id`.
export const xabChecklistClauseApi = {
  // { rows[{item_id, rule_ref, clause_id, clause_no, clause_text,
  //          regulation_name, other_numbers, note}], summary }
  suggest: (params) =>
    api
      .get("/xab/checklist-items/clause-suggest", { params })
      .then((r) => r.data),
  // { links: [{item_id, clause_id}] } → { linked, skipped[] }
  apply: (links) =>
    api
      .post("/xab/checklist-items/clause-apply", { links })
      .then((r) => r.data),
};

// ── БН-ийн АЖЛЫН БАЙРНЫ бүртгэл ────────────────────────────────────────
// Шалгалтад "Хяналт хийсэн ажлын байр"-ыг чөлөөт бичвэрийн оронд лавлахаас
// сонгоно. Журам 2.5.4 «БН-ийн БҮХ ажлын байруудад» шаардлагыг хянахад.
/** Мониторинг (байнгын хяналт) хийдэг БН-ийн лавлах — ХЭМАБМТ-06 4.3.7.
 *  `covered` нь НЭВТЭРСЭН хүний нэгж бүртгэлтэй эсэх; сонгосон ӨӨР
 *  нэгжийн хувьд `items`-ээс `coversUnit()`-ээр бодно
 *  (src/components/inspection/monitoringScope.js). */
export const xabBnMonitoringApi = {
  // { items[{id,tseh_code,bn_code,grp_code,note}], covered, can_manage }
  list: (params) =>
    api.get("/xab/bn-monitoring", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/bn-monitoring", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/bn-monitoring/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/xab/bn-monitoring/${id}`).then((r) => r.data),
};

export const xabBnWorkplaceApi = {
  // { items[{id,name,code,object_type,is_high_risk,tseh_name}], summary, access }
  list: (params) =>
    api.get("/xab/bn-workplaces", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/bn-workplaces", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/bn-workplaces/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/xab/bn-workplaces/${id}`).then((r) => r.data),
};

// ── Нэгжийн ХЯНАЛТЫН ХУУДАС (журам 2.3.2/2.4.2/2.5.3) ──────────────────
// Нэгж хавсралтын үлгэрчилсэн загвараас өөрийн хуудсаа боловсруулж,
// ХАБЭА-н зөвлөлөөр хэлэлцүүлэн, БН даргаар батлуулна. 1 жил хүчинтэй.
export const xabChecklistSheetApi = {
  // { sheets[{status,is_effective,approved_by_name,approved_date,valid_until}],
  //   summary, access }
  list: (params) =>
    api.get("/xab/checklist-sheets", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/checklist-sheets", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/checklist-sheets/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/xab/checklist-sheets/${id}`).then((r) => r.data),
  // Ноорогоос ХАБЭА-н зөвлөлд хэлэлцүүлэхээр илгээх
  submit: (id) =>
    api.post(`/xab/checklist-sheets/${id}/submit`, {}).then((r) => r.data),
  // БН даргын батламж (эсвэл буцаалт)
  approve: (id, councilNote) =>
    api
      .post(`/xab/checklist-sheets/${id}/approve`, {
        council_note: councilNote,
      })
      .then((r) => r.data),
  reject: (id, note) =>
    api
      .post(`/xab/checklist-sheets/${id}/approve`, { reject: true, note })
      .then((r) => r.data),
};

// ── Шалгах хуудасны БҮЛГИЙН лавлах ─────────────────────────────────────
// Журмын стандарт бүлэг (bn_code=null) засагдахгүй; цех өөрийн бүлэг нэмнэ.
export const xabChecklistGroupApi = {
  // { template, groups[{id,name,sort_order,bn_code,is_standard,item_count}], summary }
  list: (params) =>
    api.get("/xab/checklist-groups", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/checklist-groups", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/checklist-groups/${id}`, payload).then((r) => r.data),
  /* `force` — ЗӨВХӨН хөгжүүлэгчид: үзүүлэлттэй бүлгийг устгана.
     Үзүүлэлт нь УСТАХГҮЙ, бүлэггүй болоод үлдэнэ. */
  remove: (id, force = false) =>
    api
      .delete(`/xab/checklist-groups/${id}`, {
        params: force ? { force: 1 } : undefined,
      })
      .then((r) => r.data),
};

// ── ОБЪЕКТЫН АНГИЛАЛ — журам 2.7.5-ын 14 объект/ажлын төрлийн лавлах ──
// Зорилтот хяналтад хамрагдах объектууд. Зөвхөн ХАБЭА хэлтэс засварлана.
export const xabInspectionObjectApi = {
  // { items[{id,code,clause_no,name,group_name,usage_count,is_standard}],
  //   groups[], summary, can_manage, source }
  list: (params) =>
    api.get("/xab/inspection-objects", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/inspection-objects", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/inspection-objects/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/inspection-objects/${id}`).then((r) => r.data),
};

// ── Хяналтын УДИРДАМЖ (xab.inspection_directive) ──────────────────────
// Журам I-IV шатанд Хавсралт №2-5 маягт өгсөн ч ЗОРИЛТОТ хяналтад
// «Удирдамжаар», ЭЭЛЖИТ БУС хяналтад «—» гэж заасан тул эдгээрт бэлэн
// бланк хуудас байхгүй — удирдамж бүрд шалгах асуудлаа угсарна.
// «Төлөвлөгөөт / төлөвлөгөөт бус» ба «удирдамжийн дагуу / БН-ийн
// удирдамжаар» гэсэн 4 төрөл нь энд is_planned + level_code болж шингэнэ.
// { items[], summary, stage_options, level_options, status_options,
//   eval_options, can_manage, next_code }
export const xabInspectionDirectiveApi = {
  list: (params) =>
    api.get("/xab/inspection-directives", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/inspection-directives", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/inspection-directives/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/inspection-directives/${id}`).then((r) => r.data),

  // Удирдамжийн шалгах асуудал = чек листийн мөр
  items: (id) =>
    api.get(`/xab/inspection-directives/${id}/items`).then((r) => r.data),
  addItem: (id, payload) =>
    api.post(`/xab/inspection-directives/${id}/items`, payload).then((r) => r.data),
  updateItem: (itemId, payload) =>
    api.put(`/xab/inspection-directive-items/${itemId}`, payload).then((r) => r.data),
  removeItem: (itemId) =>
    api.delete(`/xab/inspection-directive-items/${itemId}`).then((r) => r.data),

  // Комиссын бүрэлдэхүүн — ажилтныг ТН-ээр холбоно (гараар бичихгүй).
  // addMembers нь `tns: []` массив хүлээдэг тул цех/хэсгээр гарсан
  // жагсаалтаас олон хүнийг ЗЭРЭГ нэмнэ.
  members: (id) =>
    api.get(`/xab/inspection-directives/${id}/members`).then((r) => r.data),
  addMembers: (id, payload) =>
    api.post(`/xab/inspection-directives/${id}/members`, payload).then((r) => r.data),
  updateMember: (memberId, payload) =>
    api.put(`/xab/inspection-directive-members/${memberId}`, payload).then((r) => r.data),
  removeMember: (memberId) =>
    api.delete(`/xab/inspection-directive-members/${memberId}`).then((r) => r.data),

  /** Удирдамжийн эх хувь (PDF/скан) — MinIO (inspection/directives/) */
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/xab/inspection-directives/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
  },
};

// ── Ажлын орчны лавлах (xab.work_environment) ─────────────────────────
// `bn_workplace.work_category`-ийн эх сурвалж. Өмнө нь вэб/backend хоёуланд
// хатуу кодлогдсон 7 утга байсныг DB рүү зөөв.
// { items[{id,code,name,description,color,usage_count,is_active}], summary,
//   can_manage }
export const xabWorkEnvironmentApi = {
  list: (params) =>
    api.get("/xab/work-environments", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/work-environments", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/work-environments/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/work-environments/${id}`).then((r) => r.data),
};

// ── Хяналт хийх этгээдийн лавлах (xab.inspection_actor) ───────────────
// «Энэ шалгах хуудсаар ХЭН хяналт хийх вэ». checklist_template.actor_id-аар
// хуудастай холбогдоно.
// { items[{id,code,name,full_name,sso_group,position_keyword,worker_tn,
//          sheet_count,is_active}], summary, can_manage }
export const xabInspectionActorApi = {
  list: (params) =>
    api.get("/xab/inspection-actors", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/inspection-actors", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/inspection-actors/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/inspection-actors/${id}`).then((r) => r.data),
};

// ── ӨЭА НАРЯДЫН ЛАВЛАХ САН (HRT хуучин системээс шилжүүлсэн) ─────────
// 8,581 мөр: тоног төхөөрөмж 591 · дэд ТТ 1344 · технологийн карт 129 ·
// ХАБЭА заавар 149 · шалгуур 53 · гэрээт байгууллага 194 + автомат холбоос.
export const xabHrRefApi = {
  /** Маягт нээхэд хэрэгтэй ЖИЖИГ лавлахууд нэг дуудлагаар.
   *  Том лавлах (ТТ, байгууллага) энд ОРОХГҮЙ — хайлттай тусад нь татна. */
  all: () => api.get("/xab/hr-reference").then((r) => r.data),

  devices: (params) =>
    api.get("/xab/hr-devices", { params }).then((r) => r.data),
  subdevices: (params) =>
    api.get("/xab/hr-subdevices", { params }).then((r) => r.data),
  companies: (params) =>
    api.get("/xab/hr-companies", { params }).then((r) => r.data),
  techCards: (params) =>
    api.get("/xab/hr-tech-cards", { params }).then((r) => r.data),
  instructions: (params) =>
    api.get("/xab/hr-instructions", { params }).then((r) => r.data),

  /** ⭐ Тоног төхөөрөмж сонгоход технологийн карт + ХАБЭА-н зааврыг
   *  автоматаар санал болгоно (нарядын маягтын 3.1/3.2 хэсэг).
   *  deviceIds — hr_device.legacy_id-ийн массив. */
  /** ⚠ `scope` — нэг ТТ дээр цахилгаан, механик, засвар гэх мэт ӨӨР ажил
   *  хийгддэг бөгөөд ажил бүр өөрийн заавар/карттай. Нарядын ажлын төрөл,
   *  гүйцэтгэгч БН-ийг дамжуулбал ЗӨВХӨН тэр ажилд хамаарах баримт ирнэ.
   *  Өгөөгүй бол бүгдийг (хуучин зан төлөв). */
  suggest: (deviceIds, scope = {}) =>
    api
      .post("/xab/hr-suggest", {
        device_ids: deviceIds || [],
        job_type_ids: scope.job_type_ids || undefined,
        bn_code: scope.bn_code || undefined,
        section_code: scope.section_code || undefined,
      })
      .then((r) => r.data),

  /** Заавар/картыг ДУГААРААР хайж агуулгыг нь буцаана.
   *  Наряд дээр «№35ш» гэж бичихэд ямар заавар болохыг баталгаажуулна. */
  byNo: (no, kind) =>
    api
      .get("/xab/hr-doc-by-no", { params: { no, kind: kind || "instruction" } })
      .then((r) => r.data),

  /** Гэрээт байгууллагын ажилчид — §4-т «байгууллага → ажилтан» сонголт.
   *  `unavailable: true` бол хүснэгт үүсээгүй (migration ажиллаагүй) —
   *  «ажилтан бүртгэгдээгүй» гэдгээс ӨӨР утга тул тусад нь харуулна. */
  companyWorkers: (companyLegacyId) =>
    api
      .get("/xab/hr-company-workers", {
        params:
          companyLegacyId == null ? {} : { company_legacy_id: companyLegacyId },
      })
      .then((r) => r.data),

  /** Баримт хавсаргах — kind: "instruction" | "card".
   *  HRT-д баримт өөрөө хадгалагддаггүй байсан тул ажилтан «№35ш заавар»
   *  гэдгийг хараад уншиж чаддаггүй байв. Одоо MinIO-д хавсаргана. */
  uploadDoc: (kind, id, file) => {
    const form = new FormData();
    form.append("file", file);
    return api
      .post(`/xab/hr-doc-file/${kind}/${id}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
  },
  // Батлах урсгал: action = submit | approve | return
  setDocApproval: (kind, id, action, reason) =>
    api
      .post(`/xab/hr-ref/${kind}/${id}/approval`, { action, reason })
      .then((r) => r.data),

  removeDoc: (kind, id) =>
    api.delete(`/xab/hr-doc-file/${kind}/${id}`).then((r) => r.data),

  // ── Лавлах удирдах (CRUD) ─────────────────────────────────────────
  // HRT дээр лавлах цаашид засагдахгүй тул манай систем эзэн болно.
  // kind: device | subdevice | tech-card | instruction | provision |
  //       company | permit-type | equipment-type | provision-class |
  //       subdevice-class | provision-check
  create: (kind, payload) =>
    api.post(`/xab/hr-ref/${kind}`, payload).then((r) => r.data),
  update: (kind, id, payload) =>
    api.put(`/xab/hr-ref/${kind}/${id}`, payload).then((r) => r.data),
  remove: (kind, id) =>
    api.delete(`/xab/hr-ref/${kind}/${id}`).then((r) => r.data),

  /** ⭐ ӨНДӨР ЭРСДЭЛТЭЙ АЖЛЫН ТӨРЛИЙН ЛАВЛАХ — НЭГ эх сурвалж.
   *  ⚠ Наряд бүртгэл, холбоосын хамрах хүрээ, лавлахын таб бүгд ЭНЭ
   *    дуудлагыг ашиглана. Өөр өөр газар өөрийн жагсаалт барьвал хоёр
   *    тал зөрж, нэг ажлын төрөл хоёр өөр нэртэй болно.
   *  Хадгалах газар нь `Inspection.Reference` (Type_ID=21) — id нь наряд,
   *  холбоост хадгалагдсан тул ЗӨӨХГҮЙ. */
  jobTypes: () => api.get("/xab/hr-job-types").then((r) => r.data),
  createJobType: (name) =>
    api.post("/xab/hr-job-types", { name }).then((r) => r.data),
  updateJobType: (id, name) =>
    api.put(`/xab/hr-job-types/${id}`, { name }).then((r) => r.data),
  removeJobType: (id) =>
    api.delete(`/xab/hr-job-types/${id}`).then((r) => r.data),

  /** Холбоосын ХОЁР ТАЛ — удирдах дэлгэцэд (`link_id`-тай).
   *  ⚠ `suggest` нь нарядад зориулсан, link_id буцаадаггүй тул салгах
   *    товчид ЭНЭ дуудлага хэрэгтэй. */
  deviceDocs: (deviceLegacyId) =>
    api
      .get("/xab/hr-device-docs", {
        params: { device_legacy_id: deviceLegacyId },
      })
      .then((r) => r.data),
  docDevices: (kind, docLegacyId) =>
    api
      .get("/xab/hr-device-docs", {
        params: { kind, doc_legacy_id: docLegacyId },
      })
      .then((r) => r.data),

  /** ⭐ ТТ ↔ карт/заавар/дэд ТТ холбоос — автомат саналыг тодорхойлно.
   *  link: card | safety | subdevice */
  addLink: (link, deviceLegacyId, targetLegacyId, scope = {}) =>
    api
      .post(`/xab/hr-link/${link}`, {
        device_legacy_id: deviceLegacyId,
        target_legacy_id: targetLegacyId,
        // Хамрах хүрээ — хоосон бол бүх ажил / бүх БН-д хамаарна
        job_type_id: scope.job_type_id || undefined,
        job_type_name: scope.job_type_name || undefined,
        bn_code: scope.bn_code || undefined,
        bn_name: scope.bn_name || undefined,
        section_code: scope.section_code || undefined,
        section_name: scope.section_name || undefined,
      })
      .then((r) => r.data),
  removeLink: (link, id) =>
    api.delete(`/xab/hr-link/${link}/${id}`).then((r) => r.data),
};

// ── ӨЭА/наряд урьдчилсан checklist (5A / 15 критик) — DB лавлах ───────
export const xabPreworkChecklistApi = {
  list: (type) =>
    api
      .get("/xab/prework-checklists", { params: type ? { type } : {} })
      .then((r) => r.data),
  create: (payload) =>
    api.post("/xab/prework-checklists", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/prework-checklists/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/prework-checklists/${id}`).then((r) => r.data),
  reset: (type) =>
    api
      .post("/xab/prework-checklists/reset", null, { params: { type } })
      .then((r) => r.data),
};

export const xabRefApi = {
  // Эрсдэлийн түвшин (1-5 Бага · 6-19 Дунд · 20-25 Өндөр) — xab.risk_levels
  getRiskLevels: () => api.get("/xab/risk-levels").then((r) => r.data),
  getProbabilityLevels: () =>
    api.get("/xab/probability-levels").then((r) => r.data),
  getConsequenceLevels: () =>
    api.get("/xab/consequence-levels").then((r) => r.data),
  getRiskMatrix: () => api.get("/xab/risk-matrix").then((r) => r.data),
  getHazardCategories: () =>
    api.get("/xab/hazard-categories").then((r) => r.data),
  getControlTypes: () => api.get("/xab/control-types").then((r) => r.data),
  getRiskStatuses: () => api.get("/xab/risk-statuses").then((r) => r.data),
  getInspectionTypes: () =>
    api.get("/xab/inspection-types").then((r) => r.data),

  /* Админын харагдац — идэвхгүй мөр, `protected` туг, `can_manage`-тай.
     ⚠ Хэлбэр нь ӨӨР: {items, can_manage}. Аппын бусад хэрэглэгч массив
     хүлээдэг тул `getInspectionTypes` нь хэвээр массив буцаана. */
  getInspectionTypesAdmin: () =>
    api.get("/xab/inspection-types", { params: { all: 1 } }).then((r) => r.data),
  saveInspectionType: (body) =>
    api.put("/xab/inspection-types", body).then((r) => r.data),
  deleteInspectionType: (id) =>
    api.delete("/xab/inspection-types", { params: { id } }).then((r) => r.data),

  // ── Эрсдэлийн үнэлгээний ТӨРӨЛ (ээлжит / ээлжит бус) — удирдах боломжтой ──
  getRiskAssessmentTypes: (all = false) =>
    api
      .get("/xab/risk-assessment-types", { params: all ? { all: 1 } : {} })
      .then((r) => r.data),
  createRiskAssessmentType: (body) =>
    api.post("/xab/risk-assessment-types", body).then((r) => r.data),
  updateRiskAssessmentType: (id, body) =>
    api.put(`/xab/risk-assessment-types/${id}`, body).then((r) => r.data),
  deleteRiskAssessmentType: (id) =>
    api.delete(`/xab/risk-assessment-types/${id}`).then((r) => r.data),

  // ── Эрсдэлийн үнэлгээний ЧИГЛЭЛ (5 чиглэл) — удирдах боломжтой ──
  getRiskAssessmentDirections: (all = false) =>
    api
      .get("/xab/risk-assessment-directions", { params: all ? { all: 1 } : {} })
      .then((r) => r.data),
  createRiskAssessmentDirection: (body) =>
    api.post("/xab/risk-assessment-directions", body).then((r) => r.data),
  updateRiskAssessmentDirection: (id, body) =>
    api.put(`/xab/risk-assessment-directions/${id}`, body).then((r) => r.data),
  deleteRiskAssessmentDirection: (id) =>
    api.delete(`/xab/risk-assessment-directions/${id}`).then((r) => r.data),
};

// ── Лавлах сан засварлах (admin) — CategoriesAdmin/LevelsAdmin/MatrixAdmin ──
// Эрх шаардана (create_risk_reference_threat). Backend: reference_admin.py.
export const xabRefAdminApi = {
  // Аюулын хүчин зүйл (hazard category)
  createCategory: (body) =>
    api.post("/xab/hazard-categories", body).then((r) => r.data),
  updateCategory: (id, body) =>
    api.put(`/xab/hazard-categories/${id}`, body).then((r) => r.data),
  deleteCategory: (id) =>
    api.delete(`/xab/hazard-categories/${id}`).then((r) => r.data),
  // Магадлал / Үр дагаврын түвшин (level fixed — зөвхөн үг хэллэг засна)
  updateProbability: (id, body) =>
    api.put(`/xab/probability-levels/${id}`, body).then((r) => r.data),
  updateConsequence: (id, body) =>
    api.put(`/xab/consequence-levels/${id}`, body).then((r) => r.data),
  // Эрсдэлийн матрицын нүд (өнгө/threshold)
  updateMatrix: (id, body) =>
    api.put(`/xab/risk-matrix/${id}`, body).then((r) => r.data),
};

export const xabHazardApi = {
  /* ⚠ `includeSubs` — БН-ийн нэмсэн ДЭД аюулыг ч оруулна (`1.3.1`).
     Лавлахын хуудас нь үүнийг өгөхгүй тул тэнд 100 хэвээр; эрсдэлийн
     үнэлгээ нь өгнө — БН тэдгээрийг ашигладаг (2026-10-09). */
  list: ({ categoryId, search, includeSubs } = {}) => {
    const params = {};
    if (categoryId) params.category_id = categoryId;
    if (search) params.search = search;
    if (includeSubs) params.include_subs = 1;
    return api.get("/xab/hazards", { params }).then((r) => r.data);
  },
  get: (id) => api.get(`/xab/hazards/${id}`).then((r) => r.data),
  create: (data) => api.post("/xab/hazards", data).then((r) => r.data),
  update: (id, data) => api.put(`/xab/hazards/${id}`, data).then((r) => r.data),
  delete: (id) => api.delete(`/xab/hazards/${id}`).then((r) => r.data),
};

export const xabAuditApi = {
  list: ({ tableName, action, userTn, page = 1, perPage = 50 } = {}) => {
    const params = { page, per_page: perPage };
    if (tableName) params.table_name = tableName;
    if (action) params.action = action;
    if (userTn) params.user_tn = userTn;
    return api.get("/xab/audit/log", { params }).then((r) => r.data);
  },
  today: () => api.get("/xab/audit/log/today").then((r) => r.data),
  recordHistory: (table, recordId) =>
    api.get(`/xab/audit/${table}/${recordId}`).then((r) => r.data),
};

// ============================================================================
// ХАБЭА-гийн ДҮРЭМ ЖУРМЫН ЛАВЛАХ САН (/knowledge/regulations)
//
// Заалт (clause) нь AI-ийн үндсэн орц — `searchClauses` нь журмын текстээс
// хайж, «журмын 2.7.5-д ингэж заасан» гэж эх сурвалж заахад хэрэглэгдэнэ.
// ============================================================================
// ============================================================================
// IV ШАТНЫ ХЯНАЛТЫН ЖИЛИЙН ХУВААРЬ (/inspection/plan)
//
// ХЭМАБМТ-06 журам 2.6.3.5 — «Ерөнхий инженерийн баталсан жилийн хуваарийн
// дагуу хийнэ». Хуваарийг ХАБЭА-н хэлтэс боловсруулна.
// ============================================================================
/**
 * БАЙНГЫН ХЯНАЛТ (мониторинг) — ХЭМАБМТ-06 хавсралт №6 / №7.
 *   journal — өдөр тутмын дэвтэр (журам 4.3.7)
 *   monthly — хэсэг тус бүрээр нэгтгэсэн сарын тайлан (журам 4.3.3)
 */
export const xabMonitoringApi = {
  journal: (params) =>
    api.get("/xab/monitoring-journal", { params }).then((r) => r.data),
  monthly: (params) =>
    api.get("/xab/monitoring-monthly", { params }).then((r) => r.data),
  // Маягт №01 ярилцлагын хуудас / №02 уулзалтын тэмдэглэл (А/102 хавс.№10)
  talks: (params) =>
    api.get("/xab/monitoring-talks", { params }).then((r) => r.data),
  createTalk: (data) =>
    api.post("/xab/monitoring-talks", data).then((r) => r.data),
  updateTalk: (id, data) =>
    api.put(`/xab/monitoring-talks/${id}`, data).then((r) => r.data),
  deleteTalk: (id) =>
    api.delete(`/xab/monitoring-talks/${id}`).then((r) => r.data),
  // Журам 4.5.5.1 — «аюулгүй ажиллагч»-ийн нэр дэвшигчид
  awards: (params) =>
    api.get("/xab/monitoring-talk-awards", { params }).then((r) => r.data),
};

export const xabInspectionPlanApi = {
  /** Хуваарь үүссэн оны жагсаалт (сонгогчид) */
  years: () => api.get("/xab/inspection-plans").then((r) => r.data),
  /** ОРУУЛСАН ХУВААРИЙН ЖАГСААЛТ + эрхийн профиль.
   *  ⚠ perm.stages нь ЯМАР шатны хуваарь үүсгэж болохыг хэлнэ — UI
   *    өөрөө таамаглавал backend-ийн дүрэмтэй зөрж, «үүсгэх» дарахад
   *    403 авна. */
  list: (params = {}) =>
    api.get("/xab/inspection-plans", { params }).then((r) => r.data),
  /** Тухайн оны хуваарь — мөр, гүйцэтгэл, хяналтын тоонуудын хамт */
  get: (year, params = {}) =>
    api.get(`/xab/inspection-plans/${year}`, { params }).then((r) => r.data),
  create: (data) =>
    api.post("/xab/inspection-plans", data).then((r) => r.data),
  update: (id, data) =>
    api.put(`/xab/inspection-plans/id/${id}`, data).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/inspection-plans/id/${id}`).then((r) => r.data),
  /** Лавлахын БҮХ бүтцийн нэгжийг бланкаар нөхөж нэмэх */
  fill: (id) =>
    api.post(`/xab/inspection-plans/id/${id}/fill`).then((r) => r.data),
  /** Мөр нэмэх — нэг БН эсвэл `{ items: [...] }` бөөнөөр */
  addItems: (id, data) =>
    api.post(`/xab/inspection-plans/id/${id}/items`, data).then((r) => r.data),
  updateItem: (itemId, data) =>
    api.put(`/xab/inspection-plan-items/${itemId}`, data).then((r) => r.data),
  removeItem: (itemId) =>
    api.delete(`/xab/inspection-plan-items/${itemId}`).then((r) => r.data),

  /* ── БАТАЛГААЖУУЛАЛТ (журам 2.6.3.5) ───────────────────────────────
     ХАБ хэлтэс боловсруулж → хэвлэж → Үйлдвэрийн удирдлагаар батлуулж →
     ГАРЫН ҮСЭГТЭЙ эх хувийг хавсаргасан үед л «батлагдсан» болно.
     Батлагдсаныг БҮХ БН харна; засахын тулд ноорог руу буцаана. */
  uploadApproved: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/xab/inspection-plans/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
  },
  approve: (id, data) =>
    api
      .post(`/xab/inspection-plans/id/${id}/approve`, data)
      .then((r) => r.data),
  /** Батламжийг цуцалж ноорог руу (эх хувь аудитад үлдэнэ) */
  unapprove: (id) =>
    api.delete(`/xab/inspection-plans/id/${id}/approve`).then((r) => r.data),
};

/**
 * 4-Р ШАТ ОРОХ ХУГАЦААНЫ САНУУЛГА.
 *
 * Батлагдсан жилийн хуваариас ойртсон ба хугацаа хэтэрсэн шалгалтыг өгнө.
 * Хамрах хүрээ: ХАБЭА → бүх БН; бусад (БН дарга, орлогч, ХАБ мэргэжилтэн)
 * → зөвхөн өөрийн цех. Хийгдсэнийг `xab.inspection`-ээс хасаж тооцно.
 */
export const xabInspectionPlanDueApi = {
  list: (params) =>
    api.get("/xab/inspection-plan-due", { params }).then((r) => r.data),
};

export const xabRegulationApi = {
  list: (params = {}) =>
    api.get("/xab/regulations", { params }).then((r) => r.data),
  get: (id) => api.get(`/xab/regulations/${id}`).then((r) => r.data),
  create: (data) => api.post("/xab/regulations", data).then((r) => r.data),
  update: (id, data) =>
    api.put(`/xab/regulations/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/xab/regulations/${id}`).then((r) => r.data),
  /** ӨӨРЧЛӨЛТИЙН ТҮҮХ — хэн хэзээ ЮУ өөрчилсөн (`xab.audit_log`-оос). */
  history: (id) =>
    api.get(`/xab/regulations/${id}/history`).then((r) => r.data),
  categories: () =>
    api.get("/xab/regulations/categories").then((r) => r.data),
  /**
   * Хяналтын үзүүлэлт — БҮХ САНГИЙН дүр зураг (шүүлтээс хамаарахгүй).
   *
   * ⚠ Клиент талд `rows`-оос тоолж болохгүй: тэр нь зөвхөн шүүгдсэн
   *   жагсаалтыг хамарна, мөн богино/урт заалт, давхардал зэрэг чанарын
   *   хэмжүүрийг бүх 77 мянган заалтыг татахгүйгээр бодох боломжгүй.
   */
  stats: () => api.get("/xab/regulations/stats").then((r) => r.data),
  /* ── ФАЙЛААС ЗААЛТ (2026-10-29) ─────────────────────────────────────
     Хэрэглэгчийн шаардсан ДАРААЛАЛ:
       ① analyze      — САНД БАЙГАА ЭСЭХ + файлын шинжилгээ (бичихгүй)
       ② parsePreview — задлаад харуулна (бичихгүй)
       ③ applyClauses — хэрэглэгч БАТАЛСНЫГ бичнэ
     ⚠ ①-гүйгээр ②-т орохгүй: давхардсан журмыг дахин задалвал лавлахад
       ижил заалт хоёр удаа орж, эшлэл хоёрдмол болно.
     ⚠ Скан PDF дээр ① нь `recommend:"ocr_needed"` буцаана — ② дуудахгүй. */
  analyze: (file, name) => {
    const fd = new FormData();
    fd.append("file", file);
    if (name) fd.append("name", name);
    return api
      .post("/xab/regulations/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      })
      .then((r) => r.data);
  },
  parsePreview: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/xab/regulations/parse-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      })
      .then((r) => r.data);
  },
  /* ── СКАН PDF → OCR (ФОН ажил) ─────────────────────────────────────
     ⚠ 200 хуудас ≈ 16 минут тул хүсэлт дотор багтахгүй: `ocrStart` нь
       ажил бүртгээд 202 буцна, клиент `ocrJob`-оор явцыг асууна.
     ⚠ `ocrReady` — tesseract ба ажлын хүснэгт байгаа эсэх. Байхгүй бол
       UI нь товчийг ОГТ харуулахгүй (хэрэглэгч дарж, 400 авахаас). */
  ocrReady: () => api.get("/xab/regulations/ocr-job").then((r) => r.data),
  ocrStart: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/xab/regulations/ocr-job", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      })
      .then((r) => r.data);
  },
  ocrJob: (id) =>
    api.get(`/xab/regulations/ocr-job/${id}`).then((r) => r.data),
  // { clauses:[{no,text,level,parent_no}], sections:[...], text_status }
  applyClauses: (id, payload) =>
    api
      .post(`/xab/regulations/${id}/clauses/apply`, payload)
      .then((r) => r.data),
  /* ── ХҮЧИНГҮЙ БОЛГОХ (хаах) ─────────────────────────────────────
     Нэг журам хэд хэдэн удаа шинэчлэгдэн ачаалагдсан тул «нэр
     давхардсан» мөрүүд үлддэг. `duplicates` нь бүлэглэж, аль нь
     ХАМГИЙН ШИНЭ болохыг заана (юу ч өөрчлөхгүй); `closeDuplicates`
     нь хуучныг нь шинэ рүү заан хаана. */
  duplicates: () =>
    api.get("/xab/regulations/duplicates").then((r) => r.data),
  closeDuplicates: (payload = {}) =>
    api.post("/xab/regulations/duplicates/close", payload).then((r) => r.data),
  /** Гараар хаах: { by_id?, date?, note? } */
  supersede: (id, payload = {}) =>
    api.post(`/xab/regulations/${id}/supersede`, payload).then((r) => r.data),
  /** Хаалтыг буцаах (алдаатай хаасан бол) */
  reopen: (id) => api.post(`/xab/regulations/${id}/reopen`).then((r) => r.data),
  /** PDF/Word байршуулах — MinIO (knowledge/regulations/) */
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/xab/regulations/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
  },
};

/**
 * АЛБАН ШААРДЛАГЫН ЖУРМЫН ЛАВЛАХ (2026-10-05).
 *
 * «Хэнд хаяглагдах», «биелэлтэд хяналт тавих», «дугаарлалт», «юу бичих
 * / хавсаргах» дүрмүүд нь урьд кодод ХАТУУ бичигдсэн байсныг лавлах
 * болгов — ХАБЭАХ өөрөө засна, журмын заалтыг хамт хардаг.
 * Backend: Flask/Resources/XAB/formal_req_rule.py
 */
export const xabFormalReqRuleApi = {
  /** Шатны дүрэм + журмын заалтын БҮТЭН ТЕКСТ */
  rules: () => api.get("/xab/formal-req-rules").then((r) => r.data),
  saveRule: (body) =>
    api.put("/xab/formal-req-rules", body).then((r) => r.data),
  /** Цуврал, дугаарлалтын шал, дараа олгогдох дугаар */
  series: () => api.get("/xab/formal-req-series").then((r) => r.data),
  /** ГАДНЫ байгууллагууд — албан шаардлагыг хэнд хаяглахад.
      ⚠ ХСБ-ийн байгууллагын лавлахаас (`product.Company`): гүйцэтгэгч,
        нийлүүлэгч, цэцэрлэг аль хэдийн бүртгэлтэй. Шинэ лавлах
        зохиовол хоёр жагсаалт зөрж, албан бичгийн хаяг нь аль нь үнэн
        болох нь мэдэгдэхгүй болно. */
  companies: () => api.get("/xab/meal-companies").then((r) => r.data),
  saveSeries: (body) =>
    api.put("/xab/formal-req-series", body).then((r) => r.data),
};

export const xabRegulationClauseApi = {
  list: (params = {}) =>
    api.get("/xab/regulation-clauses", { params }).then((r) => r.data),
  create: (data) =>
    api.post("/xab/regulation-clauses", data).then((r) => r.data),
  update: (id, data) =>
    api.put(`/xab/regulation-clauses/${id}`, data).then((r) => r.data),
  /**
   * Заалтыг БӨӨНӨӨР оруулах — журмын текстийг хуулж тавихад автоматаар
   * «2.7.5 …» гэсэн дугаараар задарна.
   * data: { regulation_id, text, replace }
   */
  bulk: (data) =>
    api.post("/xab/regulation-clauses/bulk", data).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/regulation-clauses/${id}`).then((r) => r.data),
};

/**
 * Үйлдвэрийн ДҮРЭМ ЖУРМЫН ЭХ СУРВАЛЖ (eOffice_EMC) — ЗӨВХӨН УНШИНА.
 *
 * Журмыг тэр систем удирдана. Бид хуулбарлахгүй — хуулбар авбал журам
 * шинэчлэгдэхэд хоцорч, ажилтан хүчингүй болсон журам уншиж буруу шийдвэр
 * гаргах эрсдэлтэй. `link` нь зөвхөн МАНАЙ давхарга (ХЭМАБМТ код, заалт)
 * үүсгэнэ — агуулга нь эх сурвалжид үлдэнэ.
 */
export const xabRuleSourceApi = {
  list: (params = {}) =>
    api.get("/xab/rule-source", { params }).then((r) => r.data),
  get: (id) => api.get(`/xab/rule-source/${id}`).then((r) => r.data),
  link: (id, data = {}) =>
    api.post(`/xab/rule-source/${id}/link`, data).then((r) => r.data),
};

/**
 * УРАМШУУЛАЛ — чадамжийн ҮНЭЛГЭЭНИЙ ҮЗҮҮЛЭЛТИЙН ЛАВЛАХ.
 *
 * Эх сурвалж: сургалтын журам А/528 (2026.08.06), IV бүлэг. Ур чадварын
 * сарын оноо нь ГГҮ-ийн нэмэгдэлтэй шууд холбогддог (4.3.1) тул шалгуурыг
 * кодод биш, лавлахаас удирдана.
 *
 * `reference()` нь бүх бүтцийг нэг дуудлагаар буцаана — хуудас нээхэд
 * хангалттай (бүрэлдэхүүн + шалгуур + түвшин + шатлал + сонголтууд).
 */
export const xabEvalRefApi = {
  reference: () => api.get("/xab/eval-reference").then((r) => r.data),
  listCriteria: (params = {}) =>
    api.get("/xab/eval-criteria", { params }).then((r) => r.data),
  createCriterion: (data) =>
    api.post("/xab/eval-criteria", data).then((r) => r.data),
  updateCriterion: (id, data) =>
    api.put(`/xab/eval-criteria/${id}`, data).then((r) => r.data),
  removeCriterion: (id) =>
    api.delete(`/xab/eval-criteria/${id}`).then((r) => r.data),
  updateCompetency: (id, data) =>
    api.put(`/xab/eval-competencies/${id}`, data).then((r) => r.data),
};

/**
 * ЭЕРЭГ УРАМШУУЛЛЫН ҮЙЛ АЖИЛЛАГАА.
 *
 * Эх сурвалж: process flow HSD-2026.vsdx → таб «урамшуулал»
 * «ЭЕРЭГ УРАМШУУЛЛЫН ПРОЦЕСС» — 11 үйл ажиллагаа × 5 эгнээ
 * (Орц → Ажилтны үйлдэл → Боловсруулалт → ERP → Гарц KPI).
 *
 * ⚠ Чадамжийн үнэлгээ (xabEvalRefApi)-ээс ӨӨР систем: тэр нь ГГҮ-ийн
 *   нэмэгдэл, энэ нь урамшууллын оноо. Огтлолцох цэгүүд `source_key`-ээр
 *   нэг эх сурвалж руу заана.
 */
export const xabRewardApi = {
  activities: () => api.get("/xab/reward-activities").then((r) => r.data),
  createActivity: (data) =>
    api.post("/xab/reward-activities", data).then((r) => r.data),
  updateActivity: (id, data) =>
    api.put(`/xab/reward-activities/${id}`, data).then((r) => r.data),
  removeActivity: (id) =>
    api.delete(`/xab/reward-activities/${id}`).then((r) => r.data),
  updateType: (id, data) =>
    api.put(`/xab/reward-types/${id}`, data).then((r) => r.data),

  /**
   * БОДИТ тооцоо — зураглалын «Гарц (KPI тоо)» эгнээг манай бүртгэлээс.
   * params: { from, to, bn_code, limit }
   *
   * ⚠ Оноо тооцохгүй — журамд заагаагүй. Зөвхөн бодит тоо (хэдэн ажилтан,
   *   хэдэн үйлдэл) гарна.
   */
  summary: (params = {}) =>
    api.get("/xab/reward-summary", { params }).then((r) => r.data),

  /**
   * «Ажилтан өөрөө шийдсэн» үзүүлэлтийн ЗАДАРГАА — ажилтан бүрээр
   * шийдсэн / баталгаажсан / буцаагдсан / хүлээгдэж буй тоо + батлагдалтын %.
   * params: { from, to, bn_code, q, limit }
   *
   * ⚠ Урамшууллын `self_fix` тоолуур нь ЗӨВХӨН баталгаажсаныг тоолдог.
   *   Энэ жагсаалт нь тэр тооны бүрэлдэхүүнийг харуулна.
   */
  selfFixSummary: (params = {}) =>
    api.get("/xab/self-fix-summary", { params }).then((r) => r.data),
};

// ── АЖИЛ ЗОГСООХ ЭРХ (SWA) ────────────────────────────────────────────
/**
 * «process flow HSD-2026.vsdx» → таб «ажил зогсоох» (page7)
 * «АЖИЛ ЗОГСООХ ЭРХЭЭ ЭДЛЭХ ПРОЦЕСС» + Gap Excel FR-05-03/04/05.
 *
 * Зогсолт нь ЭНД ҮҮСДЭГГҮЙ — 15 асуумж дээр «Тийм» сонгоход
 * `report_mode='stop_work'` аюул үүсэхэд автоматаар эхэлнэ. Энэ API нь
 * зөвхөн түүнээс ХОЙШИХ шийдвэрлэлтийг удирдана.
 */
export const xabStopWorkApi = {
  /** Шатны лавлах — нэр/өнгө/icon/дараагийн шилжилт. Клиентэд давхардуулахгүй. */
  stages: () => api.get("/xab/stop-work/stages").then((r) => r.data),

  /** params: { from, to, stage, open, bn_code, q } */
  list: (params = {}) =>
    api.get("/xab/stop-work", { params }).then((r) => r.data),

  detail: (id) => api.get(`/xab/stop-work/${id}`).then((r) => r.data),

  /**
   * Шат ахиулах.
   * data: { decision, note, photo_url, gps:{lat,lng}, participants:[tn] }
   * decision: arrive | review | resolved | escalate | terminate
   */
  advance: (id, data) =>
    api.post(`/xab/stop-work/${id}/advance`, data).then((r) => r.data),

  // ── Ажлын гүйцэтгэл — авч хэрэгжүүлсэн арга хэмжээ (FR-05-04-01) ──
  actions: (id) =>
    api.get(`/xab/stop-work/${id}/actions`).then((r) => r.data),
  addAction: (id, data) =>
    api.post(`/xab/stop-work/${id}/actions`, { source: "web", ...data })
      .then((r) => r.data),
  updateAction: (actionId, data) =>
    api.put(`/xab/stop-work/actions/${actionId}`, data).then((r) => r.data),
  removeAction: (actionId) =>
    api.delete(`/xab/stop-work/actions/${actionId}`).then((r) => r.data),
  /** «Зөрчил бүрэн арилсан» (FR-05-05-01). Гүйцэтгэгч өөрийгөө баталгаажуулж
   *  чадахгүй — backend 403 өгнө. */
  verifyAction: (actionId, data = {}) =>
    api.post(`/xab/stop-work/actions/${actionId}/verify`, data)
      .then((r) => r.data),
};

// ── АШУА-ийн ХАБЭА ӨДӨР ТУТМЫН ТАЙЛАН (14 асуумж) ────────────────────
/**
 * Эх сурвалж: «ХАБЭА-н нэгдсэн систем → Анхан шатны удирдах ажилтны тайлан».
 *
 * ГОЛ САНАА: АШУА өглөө бүр 14 тоо ГАРААР бөглөдөг ба практикт 3-14-р
 * багана бараг бүгд 0 үлддэг. Манай баазаас ГАРАХ тоог автоматаар тооцоод,
 * зөвхөн гараар оруулах шаардлагатайг л АШУА-д харуулна.
 *
 * auto_mode: full — манайхаас бүрэн гарна (засах шаардлагагүй)
 *            partial — хэсэгчлэн гарна, гүйцээж засна
 *            none — манайд эх сурвалж алга, зөвхөн гараар
 */
export const xabAshuaDailyApi = {
  // Лавлах — 14 асуумж
  questions: () =>
    api.get("/xab/ashua-daily-questions").then((r) => r.data),
  createQuestion: (data) =>
    api.post("/xab/ashua-daily-questions", data).then((r) => r.data),
  updateQuestion: (id, data) =>
    api.put(`/xab/ashua-daily-questions/${id}`, data).then((r) => r.data),
  removeQuestion: (id) =>
    api.delete(`/xab/ashua-daily-questions/${id}`).then((r) => r.data),

  // Тайлан
  /** params: { from, to, bn_code, tn, status } */
  list: (params = {}) =>
    api.get("/xab/ashua-daily", { params }).then((r) => r.data),
  /** Тухайн өдрийн маягт — авто тоо тооцоод буцаана. params: { date, tn } */
  form: (params = {}) =>
    api.get("/xab/ashua-daily/form", { params }).then((r) => r.data),
  /** data: { date, tn, note, values: { "<question_no>": <int|null> } } */
  save: (data) =>
    api.post("/xab/ashua-daily/save", data).then((r) => r.data),
  submit: (id) =>
    api.post(`/xab/ashua-daily/${id}/submit`, {}).then((r) => r.data),
  /** Илгээснийг ноорог болгож буцаах — засах боломж нээнэ */
  withdraw: (id) =>
    api.post(`/xab/ashua-daily/${id}/withdraw`, {}).then((r) => r.data),
  /** Устгах (soft delete) — зөвхөн тайлан гаргасан хүн эсвэл ХАБЭА хэлтэс */
  remove: (id) => api.delete(`/xab/ashua-daily/${id}`).then((r) => r.data),
  /** Тайлан үүсгэлгүйгээр зөвхөн авто тоог харах */
  preview: (params = {}) =>
    api.get("/xab/ashua-daily/preview", { params }).then((r) => r.data),

  /**
   * ХАМРАГДАЛТ — «хэн тайлагнасан, хэн үгүй». Цехийн ХАБ мэргэжилтэнд.
   * params: { date, bn_code, days }
   *
   * ⚠ АШУА-гийн албан бүртгэл системд байхгүй тул хүлээгдэж буй АШУА-г
   *   сүүлийн N хоногийн үйл ажиллагаанаас (ӨЭА бүртгэл / наряд) дүгнэнэ.
   */
  coverage: (params = {}) =>
    api.get("/xab/ashua-daily/coverage", { params }).then((r) => r.data),

  /**
   * САРЫН МАТРИЦ — мөр=өдөр, багана=14 асуумж (эх системийн харагдац).
   * params: { from, to, bn_code, tn }
   */
  matrix: (params = {}) =>
    api.get("/xab/ashua-daily/matrix", { params }).then((r) => r.data),
};

// ── САЙЖРУУЛАХ АРГА ХЭМЖЭЭ (PDCA) ─────────────────────────────────────
// «process flow HSD-2026.vsdx» page9 «Дата анализ хийх процесс»-ийн 4 дэх
// алхам «Сайжруулах», ISO 45001 §10.
//
// Дашбоардын сануулгыг (ж: «5А дадал 12% буурсан») хариуцагчтай, хугацаатай
// АЖИЛ болгож хувиргана. Өмнө нь шинжилгээ дүгнэлт болоод зогсдог байсан —
// PDCA-гийн зөвхөн C (Check) хэрэгжсэн байв.
// { items[], summary, open_metrics[], pdca_options, status_options,
//   priority_options, next_code }
export const xabImprovementApi = {
  list: (params) =>
    api.get("/xab/improvement-actions", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/improvement-actions", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/improvement-actions/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/improvement-actions/${id}`).then((r) => r.data),
  // Хаах — үр дүнг ЗААВАЛ бичнэ (эс бөгөөс сайжруулалт баталгаажихгүй)
  close: (id, payload) =>
    api
      .post(`/xab/improvement-actions/${id}/close`, payload)
      .then((r) => r.data),
};

// ── АЛБАН ШААРДЛАГА / АКТ ─────────────────────────────────────────────
// ХЭМАБМТ-06 журам 2.5.5-2.5.6: «III шатны үр дүнг АЛБАН ШААРДЛАГЫН
// хэлбэрээр гаргаж, зөрчигдсөн журам/дүрэм/зааврын нэр, арилгах хугацаа,
// арилгасан тэмдэглэлийг тусгаж, БН удирдлага болон шалгуулсан хэсгийн
// дарга нар гарын үсэг зурна.»
//   III шат → албан шаардлага · IV шат → + дүнгийн хурал
//   Ээлжит бус → АКТ-албан шаардлага
export const xabFormalReqApi = {
  list: (params) =>
    api.get("/xab/formal-requirements", { params }).then((r) => r.data),
  get: (id) => api.get(`/xab/formal-requirements/${id}`).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/formal-requirements", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/formal-requirements/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/formal-requirements/${id}`).then((r) => r.data),

  /** Шалгалтын зөрчлөөс АВТОМАТААР үүсгэнэ — гараар дахин бичихгүй */
  fromInspection: (inspectionId, payload) =>
    api
      .post(`/xab/formal-requirements/from-inspection/${inspectionId}`, payload || {})
      .then((r) => r.data),
  /** Ноорогоос албан ёсоор гаргах */
  issue: (id) =>
    api.post(`/xab/formal-requirements/${id}/issue`).then((r) => r.data),

  /** Зөрчил арилгасныг бүртгэх — «арилгасан тэмдэглэл» ЗААВАЛ */
  fulfilItem: (itemId, payload) =>
    api
      .post(`/xab/formal-requirement-items/${itemId}`, payload)
      .then((r) => r.data),
  updateItem: (itemId, payload) =>
    api
      .put(`/xab/formal-requirement-items/${itemId}`, payload)
      .then((r) => r.data),
  /** Гарын үсэг зурах / татгалзах (журам 2.5.6) */
  sign: (signId, payload) =>
    api
      .post(`/xab/formal-requirement-signs/${signId}/sign`, payload || {})
      .then((r) => r.data),

  /* ── ХАВСРАЛТУУД ────────────────────────────────────────────────────
     Бодит албан шаардлагад дор хаяж 2-3 баримт хавсрагддаг: шаардлагын
     бичиг, итгэмжлэгдсэн лабораторийн хэмжил сорилын дүн, гэрэл зураг,
     дараа нь биелэлтийн баримт. Толгойн нэг `file_url` хүрэлцэхгүй. */
  files: (id) =>
    api.get(`/xab/formal-requirements/${id}/files`).then((r) => r.data),
  addFile: (id, payload) =>
    api.post(`/xab/formal-requirements/${id}/files`, payload).then((r) => r.data),
  removeFile: (fileId) =>
    api.delete(`/xab/formal-requirement-files/${fileId}`).then((r) => r.data),
  /** MinIO руу хуулах — inspection/requirements/ (медиа проксид зөвшөөрөгдсөн) */
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/xab/formal-requirements/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
      .then((r) => r.data);
  },
};

/**
 * «Асуух» — журам, заавраас ӨГҮҮЛБЭРЭЭР хайх.
 *
 * Дуу хоолой: аудиог ОДОО БАЙГАА STT-руу (`transcribeSpeech`, Chimege)
 * илгээж текст болгоод, түүнийгээ энд дамжуулна. Тусдаа дуут endpoint
 * хийгээгүй — STT нь аль хэдийн ажиллаж байгаа, квот/токен нь тэнд.
 */
export const xabAskApi = {
  /** params: { q, per, source } */
  ask: (params = {}) =>
    api.get("/xab/ask", { params }).then((r) => r.data),
  /**
   * Нэг мөрийн дэлгэрэнгүй — БҮТЭН текст + эх файл.
   * Хайлтын хариунд бүтэн текст ирдэггүй (25 × 7000 тэмдэгт болно) тул
   * ажилтан дарсан мөрийнхөө бүтнийг л татна.
   */
  askItem: (params = {}) =>
    api.get("/xab/ask/item", { params }).then((r) => r.data),
  /**
   * Олдсон бичвэрийг ЯРИА болгож татна (Chimege synthesize).
   *
   * ⚠ Аудио нь нэвтрэлт шаарддаг тул `<audio src>` шууд ажиллахгүй
   *   (таг нь Authorization толгой явуулж чаддаггүй). Blob болгож
   *   татаад объект-URL үүсгэнэ.
   */
  askSpeak: (params = {}) =>
    api
      .get("/xab/ask/speak", { params, responseType: "blob" })
      .then((r) => r.data),
  /** Ажилтан хариулт тустай байсан эсэхийг үнэлнэ (зөвхөн өөрийн асуултаа) */
  askFeedback: (payload) =>
    api.post("/xab/ask/feedback", payload).then((r) => r.data),
  /** ХАБЭА-гийн хяналт — асуултын түүх, KPI, давтагдсан асуултууд */
  askLog: (params = {}) =>
    api.get("/xab/ask/log", { params }).then((r) => r.data),
  /** Асуултад хариу / зөвлөгөө бичих */
  askReply: (id, payload) =>
    api.post(`/xab/ask/log/${id}/reply`, payload).then((r) => r.data),
  /** Заалт нэмсний дараа индексийг шинэчилнэ (ХАБЭА) */
  reindex: () => api.post("/xab/ask/reindex").then((r) => r.data),
};

/** Зөрчлийн бүртгэлийн хяналтын самбар — /incident/dashboard.
 *  Зөвхөн ДАНГААР бүртгэсэн зөрчил (ZR-); хамрах хүрээ жагсаалттай ижил. */
export const xabViolationDashboardApi = {
  get: (params) =>
    api.get("/xab/violation-dashboard", { params }).then((r) => r.data),
};

/** Хяналт шалгалтын ТАЙЛАН — шалгалт тус бүрээр + нэгдсэн (/incident/reports).
 *  Хамрах хүрээ, хугацааны тайлбар нь самбартай ижил. */
/** Зөрчлийн ТАЙЛАН — нэгдсэн + зөрчлийн төрөл тус бүрээр (/incident/reports).
 *  ⚠ Зөрчлийн бүртгэл (ZR-) — хяналт шалгалт (INS-) БИШ. */
export const xabViolationReportApi = {
  get: (params) =>
    api.get("/xab/violation-report", { params }).then((r) => r.data),
};

export const xabInspectionReportApi = {
  get: (params) =>
    api.get("/xab/inspection-report", { params }).then((r) => r.data),
};

/**
 * ХЭМАБ-ЫН САРЫН ТАЙЛАН — 39 үзүүлэлт × 25 бүтцийн нэгж.
 *
 * Эх маягт: `тайлангууд/ХЭМАБ-ын сарын тайлан. 7.xlsx`. Backend нь
 * ЗӨВХӨН автомат бүртгэлээс бодогдох 24 мөрийг тоолно; гараар бөглөх
 * 15 мөр `manual: true`-гээр ХООСОН ирнэ (сургалт, зөвлөл, амлалт…).
 */
export const xabMonthlyReportApi = {
  get: (params) =>
    api.get("/xab/monthly-report", { params }).then((r) => r.data),
  // Гараар бөглөх мөрүүд: { year, month, cells:[{no, tseh_code, value}] }
  save: (body) =>
    api.put("/xab/monthly-report", body).then((r) => r.data),
  /**
   * Тайлангийн ТОЛГОЙ — батламж ба гарын үсэг.
   * { year, month, head:{approved_position, approved_name, approved_date,
   *                      agreed_*, checked_*, made_*, footer_note} }
   * ⚠ Зөвхөн бүрэн эрхтэй (ХАБЭА хэлтэс) — сервер 403 буцаана.
   */
  saveHead: (year, month, head) =>
    api.put("/xab/monthly-report", { year, month, head }).then((r) => r.data),
};

/**
 * ҮЙЛ АЖИЛЛАГААНЫ ЛАВЛАХ — журмаар тогтоосон давтамжит ажил ба ХУГАЦАА.
 *
 * ХЭМАБМТ-15 5.8: «тайланг хугацаанд нь гаргаж буй эсэхэд ХАБЭА-н хэлтэс
 * хяналт тавина». Тэр хяналт гар аргаар хийгддэг байсныг системжүүлнэ:
 * мөр бүр журмын ЗААЛТ руу заадаг тул сануулга авсан хүн «яагаад»
 * гэдгийг шууд уншина.
 */
export const xabActivityApi = {
  list: (params = {}) =>
    api.get("/xab/activity-schedule", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/activity-schedule", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/activity-schedule/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/activity-schedule/${id}`).then((r) => r.data),
  /** Сануулга — хугацаа ойртсон ба хэтэрсэн (хэрэглэгчийн хамрах хүрээгээр) */
  due: () => api.get("/xab/activity-schedule/due").then((r) => r.data),
};

/** ЭРСДЭЛИЙН ҮНДСЭН АНГИЛАЛ — компанийн 8 ангилалтай нийцүүлэх лавлах */
export const xabRiskCategoryApi = {
  list: (params = {}) =>
    api.get("/xab/risk-main-categories", { params }).then((r) => r.data),
  create: (payload) =>
    api.post("/xab/risk-main-categories", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/xab/risk-main-categories/${id}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/xab/risk-main-categories/${id}`).then((r) => r.data),
};

/**
 * АЖИЛТНЫ ЧАДАМЖИЙН ҮНЭЛГЭЭ — БОДИТ БҮРТГЭЛ (журам А/528, IV бүлэг).
 *
 * ⚠ `xabEvalRefApi`-ЭЭС ӨӨР: тэр нь ШАЛГУУР (журмын бичвэр), энэ нь ХЭН
 *   ХЭДЭН ОНОО АВСАН. Хоёрыг хольвол журам шинэчлэгдэхэд хуучин үнэлгээ
 *   гажна.
 *
 * Үе: ур чадвар САР БҮР (4.3.1, ГГҮ-ийн нэмэгдэл), хандлага ЖИЛД НЭГ
 * УДАА (4.4.1). `month`-ыг зөвхөн сарын чадамжид илгээнэ.
 */
export const xabEvalAssessApi = {
  /** params: { competency, year, month, tseh_code, bn_code, grp_code, status, q } */
  list: (params = {}) =>
    api.get("/xab/eval-assessments", { params }).then((r) => r.data),
  detail: (id) => api.get(`/xab/eval-assessments/${id}`).then((r) => r.data),
  /** payload: { competency, year, month, worker_tn, items[], is_zeroed, zero_reason, note, submit } */
  save: (payload) =>
    api.post("/xab/eval-assessments", payload).then((r) => r.data),
  approve: (id) =>
    api.post(`/xab/eval-assessments/${id}`, {}).then((r) => r.data),
  remove: (id) => api.delete(`/xab/eval-assessments/${id}`).then((r) => r.data),
  /** Нэгдсэн чадамж (4.5.1 жингээр) — params: { year, tseh_code, bn_code, grp_code } */
  competence: (params = {}) =>
    api.get("/xab/eval-competence", { params }).then((r) => r.data),
};

export default api;
